import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Headphones, ChevronRight, CheckCircle2, XCircle } from 'lucide-react';
import taskService from '../services/taskService';
import { chatService } from '../services/chatService';
import { useTranslation } from '../i18n/LocaleContext';

// 通知底部弹窗：客服消息（未读数）+ 近7天订单审核结果（通过/被拒数量）
const NotificationModal = ({ isOpen, onClose, unread }) => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState({ items: [], counts: { approved: 0, rejected: 0 } });
  const [lastChat, setLastChat] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setTimeout(() => setAnimating(true), 10);
      load();
    } else {
      setAnimating(false);
      setTimeout(() => setVisible(false), 300);
    }
  }, [isOpen]);

  const load = async () => {
    setLoading(true);
    try {
      const [rv, chat] = await Promise.all([
        taskService.getReviews(),
        chatService.getChatHistory(30).catch(() => ({ messages: [] })),
      ]);
      setReviews({
        items: rv.items || [],
        counts: rv.counts || { approved: 0, rejected: 0 }
      });
      const adminMsgs = (chat.messages || []).filter((m) => m.sender_role === 'admin');
      setLastChat(adminMsgs.length ? adminMsgs[adminMsgs.length - 1] : null);
    } catch (error) {
      console.error('Load notifications error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fmtTime = (value) => new Date(value).toLocaleString(
    language === 'zh' ? 'zh-CN' : language === 'en' ? 'en-US' : language === 'ko' ? 'ko-KR' : language === 'ja' ? 'ja-JP' : 'vi-VN',
    { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }
  );

  return (
    <>
      {visible && (
        <div className="fixed inset-0 z-[60]">
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${animating ? 'opacity-50' : 'opacity-0'}`}
            onClick={onClose}
          />
          <div
            className={`absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-lift flex flex-col max-h-[80vh] transform transition-transform duration-300 ease-out ${
              animating ? 'translate-y-0' : 'translate-y-full'
            }`}
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-3">
              <h2 className="text-[16px] font-bold text-ink">{t('notifTitle')}</h2>
              <button
                onClick={onClose}
                className="p-2 -mr-2 rounded-full text-ink-muted hover:bg-canvas transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 pb-6">
              <button
                type="button"
                onClick={() => { onClose(); navigate('/cskh'); }}
                className="w-full flex items-center gap-3 p-3.5 rounded-xl bg-canvas active:bg-gray-100 transition-colors text-left"
              >
                <span className="w-10 h-10 shrink-0 rounded-full bg-white flex items-center justify-center">
                  <Headphones className="w-[19px] h-[19px] text-ink" strokeWidth={1.9} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-1.5">
                    <span className="text-[13.5px] font-bold text-ink">{t('notifSupport')}</span>
                    {unread > 0 && (
                      <span className="min-w-[16px] h-4 px-1 rounded-full bg-rose text-white text-[10px] font-bold flex items-center justify-center tabular-nums">
                        {unread > 99 ? '99+' : unread}
                      </span>
                    )}
                  </span>
                  <span className="block mt-0.5 text-[11.5px] text-ink-muted truncate">
                    {lastChat ? (lastChat.message || t('notifImage')) : t('notifNoChat')}
                  </span>
                </span>
                <ChevronRight className="w-[17px] h-[17px] text-ink-muted shrink-0" strokeWidth={2} />
              </button>

              <div className="mt-5 flex items-center justify-between">
                <p className="text-[14px] font-bold text-ink">{t('notifOrders')}</p>
                <div className="flex items-center gap-1.5">
                  <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-600 tabular-nums">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {t('notifApproved')} {reviews.counts.approved}
                  </span>
                  <span className="flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-bold text-red-500 tabular-nums">
                    <XCircle className="w-3.5 h-3.5" />
                    {t('notifRejected')} {reviews.counts.rejected}
                  </span>
                </div>
              </div>
              <p className="mt-0.5 text-[11px] text-ink-muted">{t('notifRecent7')}</p>

              {loading ? (
                <p className="py-8 text-center text-[12.5px] text-ink-muted">{t('loading')}</p>
              ) : reviews.items.length === 0 ? (
                <p className="py-8 text-center text-[12.5px] text-ink-muted">{t('notifNoReviews')}</p>
              ) : (
                <div className="mt-1">
                  {reviews.items.map((item) => {
                    const approved = item.status === 'approved';
                    return (
                      <div key={item.id} className="flex items-start gap-3 py-3 border-b border-navy/[0.06] last:border-0">
                        {approved ? (
                          <CheckCircle2 className="w-[18px] h-[18px] mt-0.5 shrink-0 text-emerald-600" />
                        ) : (
                          <XCircle className="w-[18px] h-[18px] mt-0.5 shrink-0 text-red-500" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-semibold text-ink truncate">{item.title}</p>
                          <p className="mt-0.5 text-[11px] text-ink-muted tabular-nums">{fmtTime(item.reviewed_at)}</p>
                          {!approved && item.reject_reason && (
                            <p className="mt-0.5 text-[11.5px] text-red-500">{item.reject_reason}</p>
                          )}
                        </div>
                        {approved && (
                          <span className="text-[13px] font-extrabold text-emerald-600 tabular-nums shrink-0">
                            +${Number(item.reward).toFixed(2)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationModal;
